import SSPContentExt from 'sspcontentext';
import React, {
    useCallback,
    useEffect,
    useReducer,
    useRef,
    useState,
} from 'react';
import Context from './context';
import {
    INITIAL_STATE,
    reducer,
    SET_CONTENT_ACTION,
    SET_SSP_ACTION,
} from './store';
import checkForUpdates from './checkForUpdates.ts';
import { customFetch } from '../customFetch.ts';
import { getSetup } from '../getSetup.ts';
import ErrorPage from '../../components/ErrorPage';
import { fetchAccessToken } from '../fetchAccessToken.ts';
import { IContentsResponse } from 'sspcontentext/dist/model/interface/IContentsResponse';
import { openLatestDeliveryCache } from '../cacheVersionHelper.ts';
import SSPContentVO from 'sspcontentext/src/model/SSPContentVO';

let initialPageLoad = false;

let progress: HTMLDivElement | null = null;

function SspContextProvider({
    children,
    prepareOfflineUse,
    setupLink,
    showLabel,
}: {
    children: React.ReactNode;
    prepareOfflineUse: boolean;
    setupLink: string;
    showLabel?: boolean;
}): React.ReactElement {
    const params = new URLSearchParams(window.location.search);
    const setup = getSetup();
    let device = params.get('device') as string;
    let url = '';
    let pricesheetUid: string = '';
    const devMode = params.get('dev') === 'true';

    let fetchOptions: RequestInit | undefined = {
        method: 'GET',
        headers: {},
    };

    const [accessToken, setAccessToken] = useState('');

    if (setup && (setup.market.qsUrl || setup.market?.prodUrl)) {
        url =
            localStorage.getItem('use_qs') === 'true'
                ? setup.market.qsUrl
                : setup.market?.prodUrl;

        device = setup.device;
    } else if (setup) {
        return <ErrorPage msg={'Incorrect Setup'} link={setupLink} />;
    } else {
        return <ErrorPage msg={'No Setup Found'} link={setupLink} />;
    }

    if (!progress && prepareOfflineUse) {
        progress = document.createElement('div');
        progress.setAttribute('class', 'caching-progress');
        document.getElementsByTagName('body')[0].append(progress);
    }

    /**
     * state for global ssp context
     * provider
     */
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
    const [error, setError] = useState<{
        msg: string;
        status?: number | null;
    } | null>(null);

    async function cache(fetchURL: string, type?: string): Promise<string> {
        const cacheName = 'mpa-webapp-assets';

        const check = await caches.open(cacheName).then(cache =>
            cache.match(fetchURL).then(
                res => res !== undefined,
                () => false
            )
        );

        if (devMode || check) {
            return new Promise<string>(resolve => {
                resolve(fetchURL);
            });
        }

        return new Promise<any>(resolve => {
            const token =
                accessToken || localStorage.getItem('accesstoken') || undefined;
            customFetch(fetchURL, token, type)
                .then(async fetchResponse => {
                    if (!(
                        fetchResponse.status !== 204 &&
                        fetchResponse.status !== 206
                    )) {
                        console.warn(
                            `failed to cache ${fetchURL} with status ${fetchResponse.status}`
                        );
                    } else {
                        caches
                            .open(cacheName)
                            .then(cache => cache.put(fetchURL, fetchResponse))
                            .then(() => resolve(fetchURL));
                    }
                })
                .catch((error: any) => {
                    console.log(
                        `SSPContextProvider - failed to fetch ${fetchURL}`,
                        error
                    );
                });
        });
    }

    const deliveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    function handleResponse(response: Response, url: string, token: string) {
        if (response.status === 200) {
            return caches
                .open('did-webapp-precache')
                .then(cache => cache.match(url))
                .then(() =>
                    response
                        .json()
                        .catch(reason => setError({ msg: `${reason} ${url}` }))
                );
        } else if (response.status === 204) {
            const MAX_DELAY = 15000;
            let delay = 3000;
            const ra = response.headers.get('Retry-After');
            delay =
                ra && !Number.isNaN(+ra) && +ra > 0
                    ? Math.min(MAX_DELAY, +ra * 1000)
                    : Math.min(MAX_DELAY, delay);

            deliveryTimerRef.current = setTimeout(() => {
                customFetch(url, token)
                    .then(resp => handleResponse(resp, url, token))
                    .catch(err => setError({ msg: `${err}`, status: 0 }));
            }, delay);

            return null;
        } else if (response.status === 400) {
            console.warn('[SSP] 400 received → trying cache fallback:', url);
            openLatestDeliveryCache().then(cache => {
                if (cache) {
                    cache
                        .match(url)
                        .then(cachedResponse => {
                            if (cachedResponse) {
                                console.log(
                                    '[SSP] Using cached response for:',
                                    url
                                );
                                return cachedResponse.json();
                            } else {
                                console.warn('[SSP] No cache found for:', url);
                                setError({ msg: url, status: response.status });
                                return null;
                            }
                        })
                        .catch(err => {
                            console.error('[SSP] Cache fallback failed:', err);
                            setError({ msg: url, status: response.status });
                        });
                } else {
                    console.warn('[SSP] No delivery cache found');
                }
            });
        } else {
            setError({ msg: url, status: response.status });
        }
    }

    function initWithJsonURL(
        ext: SSPContentExt,
        jsonURL: string,
        accessToken: string
    ): Promise<any> {
        return customFetch(jsonURL, accessToken)
            .then(resp => handleResponse(resp, jsonURL, accessToken))
            .catch(err => setError({ msg: `${err}`, status: 0 }))
            .then(
                async (json: IContentsResponse | Array<object> | undefined) => {
                    if (json) {
                        let models: Array<object>;
                        if (Array.isArray(json)) {
                            models = json;
                        } else {
                            models = json.Contents;
                            pricesheetUid = json.RootContent;
                        }

                        ext.initWithModels(models);

                        let cached = 0;
                        if (prepareOfflineUse)
                            await Promise.all(
                                models.map(async (asset: any) => {
                                    if (
                                        (asset.ContentType === 'assetImage' ||
                                            asset.ContentType ===
                                                'assetDocument' ||
                                            asset.ContentType ===
                                                'assetUnknown') &&
                                        asset.AssetDataSet !== null
                                    ) {
                                        const fetchURL = ext.getAssetUrl(
                                            asset.AssetDataSet
                                                .AssetFileProperties.FileHash
                                        );
                                        await cache(
                                            fetchURL,
                                            asset.AssetDataSet
                                                .AssetFileProperties
                                                .FileType === 'svg'
                                                ? 'image/svg+xml'
                                                : undefined
                                        );
                                    }
                                    if (asset.ContentType === 'assetVideo') {
                                        // if video caches additional thumbnail image
                                        const fetchURL = ext.getAssetUrl(
                                            asset.AssetDataSet
                                                .AssetFileProperties.FileHash
                                        );
                                        await cache(fetchURL);
                                        if (
                                            asset.AssetDataSet
                                                .AssetFileProperties.Size <=
                                            300000000
                                        ) {
                                            const videoFetchURL =
                                                ext.getAssetUrl(
                                                    asset.AssetDataSet
                                                        .AssetFileProperties
                                                        .FileHash
                                                );
                                            await cache(videoFetchURL);
                                        }
                                    }

                                    cached += 1;
                                    if (progress) {
                                        progress.textContent =
                                            cached === models.length
                                                ? ''
                                                : `${cached}/${models.length}`;

                                        if (
                                            cached === models.length &&
                                            localStorage.getItem(
                                                'new-build-info'
                                            ) === 'true'
                                        ) {
                                            localStorage.removeItem(
                                                'new-build-info'
                                            );
                                            // addParam('update', 'true');
                                        }
                                    }
                                })
                            );
                    }
                }
            )
            .catch(err => {
                Promise.reject(err);
            });
    }

    async function tryInit(
        ext: SSPContentExt,
        accessToken: string,
        url: string
    ) {
        if ('serviceWorker' in navigator) {
            const hasSW =
                (await navigator.serviceWorker.getRegistrations()).length > 0;
            if (hasSW) {
                await initWithJsonURL(ext, url, accessToken);
            }
        } else {
            await initWithJsonURL(ext, url, accessToken);
        }
    }

    /**
     * inits ssp to load data
     * from the server
     */
    const initSSP = async () => {
        try {
            /**
             * create class that handles the SSPContentExt
             */
            const ext = SSPContentExt.instance;
            if (device && !devMode) console.log('check for updates');
            checkForUpdates(url, fetchOptions, showLabel);

            const sspWeb = {
                ext,
                init: async () => {
                    try {
                        const expiresAt = localStorage.getItem(
                            'accesstoken_expires_at'
                        );

                        const isExpired =
                            !expiresAt || Date.now() > Number(expiresAt);

                        const token =
                            navigator.onLine && isExpired
                                ? await fetchAccessToken()
                                : localStorage.getItem('accesstoken');
                        setAccessToken(token);

                        ext.configure(url, true);
                        const contentsUrl =
                            device && setup.pinpairingActivationResponse
                                ? `${url}/module/delivery/bpca/api/v2/contents?fields-preset=full`
                                : `${url}/module/delivery/bpca/api/v2/contents?client_id=${device}&fields-preset=full}`;
                        const resp = await fetch(contentsUrl, {
                            method: 'GET',
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                                Authorization: `Bearer ${token}`,
                            },
                        });
                        if (resp.status === 400) {
                            console.warn(
                                '[SSP] Initial fetch 400 → fallback to cache'
                            );

                            // skip parsing → go directly to cached init
                            await tryInit(ext, token, contentsUrl);
                            return;
                        }
                        let response;
                        try {
                            response = await resp.clone().json();
                        } catch (e) {
                            console.warn(
                                '[SSP] JSON parse failed → fallback to cache'
                            );

                            await tryInit(ext, token, contentsUrl);
                            return;
                        }
                        const limit =
                            response.Pagination.Total > 1000
                                ? response.Pagination.Total
                                : localStorage.getItem('contents_limit') ||
                                  1500;
                        localStorage.setItem(
                            'contents_limit',
                            limit.toString()
                        );

                        await tryInit(
                            ext,
                            token,
                            device && setup.pinpairingActivationResponse
                                ? `${url}/module/delivery/bpca/api/v2/contents?fields-preset=full&limit=${limit}`
                                : `${url}/module/delivery/bpca/api/v2/contents?client_id=${device}&fields-preset=full&limit=${limit}`
                        );
                    } catch (e) {
                        console.error(`Couldn't initialize SSP. Error: ${e}`);
                    }
                },
            };

            await sspWeb.init();
            return sspWeb;
        } catch (error) {
            console.error("Couldn't initialize SSP", error);
            throw new Error(`${error}`);
        }
    };

    /**
     * retrieves the initial content from ssp
     */
    const handleInitialPageLoad = useCallback(async () => {
        try {
            /**
             * set the language for ssp provider by given
             * url param
             */
            let sspExt = await initSSP();

            /**
             * load data from ssp and pass it to
             * the ssp provider store
             */

            if (sspExt) {
                let priceSheet: SSPContentVO | null = null;

                if (pricesheetUid) {
                    priceSheet = sspExt.ext.getContentModel(pricesheetUid);
                } else if (
                    sspExt.ext.projectModel &&
                    sspExt.ext.projectModel.asModel.contentTreeChildren.length >
                        0
                ) {
                    priceSheet =
                        sspExt.ext.projectModel.asModel.contentTreeChildren[0];
                }

                if (priceSheet) {
                    dispatch({ type: SET_CONTENT_ACTION, value: priceSheet });
                    dispatch({ type: SET_SSP_ACTION, value: sspExt.ext });
                }
            } else {
                const retrySSPConnectionInterval = setInterval(async () => {
                    sspExt = await initSSP();

                    if (!sspExt) {
                        clearInterval(retrySSPConnectionInterval);
                    } else {
                        dispatch({ type: SET_SSP_ACTION, value: sspExt.ext });
                    }
                }, 1000);
            }
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        if (!initialPageLoad) {
            initialPageLoad = true;
            handleInitialPageLoad();
        }
    }, [handleInitialPageLoad]);

    const getContextValue = useCallback(
        () => ({
            state,
            dispatch,
        }),
        [state, dispatch]
    );

    if (error) {
        return (
            <ErrorPage
                msg={error.msg}
                status={error.status || undefined}
                setup={setup}
                link={setupLink}
            />
        );
    }

    return (
        <Context.Provider value={getContextValue()}>
            {children}
        </Context.Provider>
    );
}

export default SspContextProvider;
